import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import Bugsnag from '@bugsnag/browser';
import {
  AppDispatch,
  changeLocation,
  RootState,
  getClient,
  bearerAuth,
  validationErrors,
  store,
} from '@src/core/store';
import { setProcessedSale } from '@src/stores/sale';
import { setCurrentOrder, setProcessedOrder, loadOrderAsCurrent, retrieveAllVouchersAsync } from '@src/features/orders';
import { loadSaleForDirectLink, setDirectLink, setProcessedDirectLink } from '@src/stores/directLink';
import { setCurrentSaleChange, setProcessedSaleChange } from '@src/stores/saleChange';
import handleExternalAuthentication from '@src/features/payments/external-auth/handleExternalAuthentication';
import { clearSaleOrigin } from '@src/common';
import { publicApiTokenSelector } from '@src/stores/app';
import * as storage from '@src/common/storage';
import {
  Order,
  Sale,
  DirectLink,
  SaleChange,
  TransactionRequest,
  PaymentCardParams,
  PaymentMethodExternalAuthentication,
  PaymentOption,
  Transaction,
  Buyer,
} from '@src/core/buyerApiClient';
import { AxiosResponse, AxiosError } from 'axios';
import { globalParameterSelector } from '@src/stores/globalParameters';
import type {
  ExternalAuthenticationProviderParams,
  ExternalAuthResult,
} from '@src/features/payments/external-auth/types';
import { ExternalAuthenticationProviderFactory } from '@src/features/payments/external-auth/factory';
import {
  CheckoutErrorsEnum,
  isMissingExecutionRule,
  requiresBuyerToken,
  shouldUseExternalAuthentication,
} from '@src/domains/payments';
import type { Transactable } from '@src/domains/transactables';
import { resolveTransactableKey } from '@src/features/payments/core/resolveTransactableKey';
import { classifyTransactionError, isFailedTransaction, requiresRedirect } from '@src/domains/transactions';

const planneWidgetSaleReturnKey = 'planneWidgetSaleReturn';

export interface PaymentState {
  paymentOptions: { [key: string]: PaymentOptionsLoad };
  paymentOngoing: boolean;
  paymentErrors: { [key: string]: string[] };
  lastSuccessfulPayment?: number;
  skipExternalAuth: boolean;
  externalAuthChallengeCount: number;
  chargeAttemptCount: number;
  /**
   * The external authentication in course: the provider's setup before the transact, or the challenge a
   * transaction came back waiting on. Its type picks the challenge modal that is mounted.
   */
  externalAuthentication?: PaymentMethodExternalAuthentication;
}

interface PaymentOptionsLoad {
  list: PaymentOption[];
  loading: boolean;
}

const initialState: PaymentState = {
  paymentOptions: {},
  paymentOngoing: false,
  paymentErrors: {},
  skipExternalAuth: false,
  externalAuthChallengeCount: 0,
  chargeAttemptCount: 0,
};

// Reducers

export const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    setPaymentOptionsLoad: {
      reducer(
        state,
        {
          payload: list,
          meta: { key, loading },
        }: PayloadAction<PaymentOption[], string, { key: string; loading: boolean }>,
      ) {
        state.paymentOptions[key] = { list, loading };
      },
      prepare(pms: PaymentOption[], key: string, loading = false) {
        return { payload: pms, meta: { key, loading } };
      },
    },
    setPaymentOngoing: (state: PaymentState, action: PayloadAction<boolean, string>) => {
      state.paymentOngoing = action.payload;
    },
    setPaymentErrors: (state: PaymentState, action: PayloadAction<{ [key: string]: string[] }, string>) => {
      state.paymentErrors = action.payload;
    },
    setLastSuccessfulPayment: (state: PaymentState, action: PayloadAction) => {
      state.lastSuccessfulPayment = Date.now();
    },
    setSkipExternalAuth: (state: PaymentState, action: PayloadAction<boolean>) => {
      state.skipExternalAuth = action.payload;
    },
    markExternalAuthChallengeShown: (state: PaymentState) => {
      state.externalAuthChallengeCount += 1;
    },
    markChargeAttempt: (state: PaymentState) => {
      state.chargeAttemptCount += 1;
    },
    resetPaymentAttempts: (state: PaymentState) => {
      state.chargeAttemptCount = 0;
      state.externalAuthChallengeCount = 0;
    },
    setExternalAuthentication: (
      state: PaymentState,
      action: PayloadAction<PaymentMethodExternalAuthentication | undefined>,
    ) => {
      state.externalAuthentication = action.payload;
    },
    triggerExternalAuthStatus: (_: PaymentState, __: PayloadAction<string, string>) => {},
    triggerExternalAuthError: () => {},
  },
});

export const {
  setPaymentOptionsLoad,
  setPaymentOngoing,
  setPaymentErrors,
  setLastSuccessfulPayment,
  setSkipExternalAuth,
  markExternalAuthChallengeShown,
  markChargeAttempt,
  resetPaymentAttempts,
  setExternalAuthentication,
  triggerExternalAuthStatus,
  triggerExternalAuthError,
} = paymentSlice.actions;

// Selectors

export const paymentOngoingSelector = (state: RootState) =>
  state.payment.paymentOngoing ||
  (state.payment.lastSuccessfulPayment != null && Date.now() - state.payment.lastSuccessfulPayment < 10000); // Workaround to keep displaying the loading spinner for a while after a successful payment, so things can load

/**
 * The charge itself, without the 10s tail `paymentOngoingSelector` keeps for the spinner. That tail
 * is wall-clock, so it only stops reading true when something else re-renders — fine for a spinner,
 * wrong for anything that has to start back up on its own.
 */
export const paymentInFlightSelector = (state: RootState) => state.payment.paymentOngoing;

export const paymentErrorsSelector = (state: RootState) => state.payment.paymentErrors;

export const externalAuthChallengeCountSelector = (state: RootState) => state.payment.externalAuthChallengeCount;

export const chargeAttemptCountSelector = (state: RootState) => state.payment.chargeAttemptCount;

export const externalAuthenticationSelector = (state: RootState) => state.payment.externalAuthentication;

// Actions

export const clearPaymentErrors = () => (dispatch: AppDispatch) => dispatch(setPaymentErrors({}));

type ExternalAuthDecisionParams = {
  widgetUrl: string;
  paymentOption: PaymentOption;
  transactable: Transactable;
  data: TransactionRequest;
  maxAttempts: number;
  dispatch: AppDispatch;
  buyer?: Buyer;
};

export const externalAuthDecision = async (params: ExternalAuthDecisionParams): Promise<ExternalAuthResult> => {
  const { widgetUrl, paymentOption, transactable, data, maxAttempts, dispatch } = params;
  const { sale, order, directLink, saleChange } = transactable;

  const state = store.getState();
  const {
    auth: { buyer, buyerToken },
  } = state;
  // The card only matters to providers that tokenize it before their setup, as Malga does; the others
  // ignore it.
  const { data: paymentMethodExternalAuthentication } = await getClient(
    state,
  ).paymentMethods.getExternalAuthenticatePaymentMethod(
    paymentOption.paymentMethod.id,
    { paymentCard: data.paymentCard },
    bearerAuth(buyerToken!),
  );

  // Mounts the provider's modal; the providers wait for or look up their containers in it
  dispatch(setExternalAuthentication(paymentMethodExternalAuthentication));

  try {
    return await handleExternalAuthentication({
      widgetUrl,
      paymentOption,
      transactable: {
        sale,
        order,
        directLink,
        saleChange,
      },
      data,
      paymentMethodExternalAuthentication,
      maxAttempts,
      dispatch,
      buyer,
    });
  } finally {
    dispatch(setExternalAuthentication(undefined));
  }
};

export interface Payment {
  order?: Order;
  sale?: Sale;
  directLink?: DirectLink;
  saleChange?: SaleChange;
  paymentCard?: PaymentCardParams;
  paymentMethodId?: string;
  externalAuthentication?: PaymentMethodExternalAuthentication;
}

const updateDirectLinkAfterTransaction = async (
  directLink: DirectLink,
  dispatch: AppDispatch,
  getState: () => RootState,
) => {
  const { data: newDirectLink } = await getClient(getState()).directLinks.getDirectLink(directLink.code);
  if (newDirectLink) {
    dispatch(setProcessedDirectLink(newDirectLink));
  }

  return newDirectLink;
};

type OrderReloadSetter = typeof setCurrentOrder | typeof setProcessedOrder;

// The charged order has vouchers now, but GetOrderVouchers checks ownership, and whoever pays an order link is
// rarely the order's customer: a failure there must not hold back the confirmed order.
const reloadLinkOrder = async (
  orderId: string,
  setter: OrderReloadSetter,
  dispatch: AppDispatch,
  getState: () => RootState,
) => {
  const state = getState();
  const { data: order } = await getClient(state).orders.getOrder(orderId);

  const vouchers = state.auth.buyerToken ? await retrieveAllVouchersAsync(getState, order).catch(() => []) : [];

  dispatch(setter(order, vouchers));

  return order;
};

const reloadOrder = (
  tr: { order: Order; directLink?: DirectLink },
  setter: OrderReloadSetter,
  dispatch: AppDispatch,
  getState: () => RootState,
) =>
  requiresBuyerToken(tr)
    ? loadOrderAsCurrent(dispatch, getState, tr.order.id, setter)
    : reloadLinkOrder(tr.order.id, setter, dispatch, getState);

const getErrorMessageByType = (error: AxiosError<any>) => {
  const statusCode = error.response?.status;
  const errorType = statusCode === 400 ? error.response!.data?.errors[0]?.extra?.type : undefined;

  return classifyTransactionError(statusCode, errorType);
};

interface TransactableConfig {
  paymentMethodId: string;
  executeTransaction: (client: ReturnType<typeof getClient>) => Promise<AxiosResponse<Transaction>>;
  executeNotify: (client: ReturnType<typeof getClient>, errors: Record<string, unknown>) => Promise<void>;
  storageId: string;
  /** Reloads the transactable into the store and answers with its last transaction. */
  refreshTransactable: () => Promise<Transaction | null>;
  shouldReloadOnValidationError: boolean;
}

/** What a provider needs besides the challenge itself, when a transaction comes back waiting on the buyer. */
type ChallengeContext = Omit<ExternalAuthenticationProviderParams, 'paymentMethodExternalAuthentication'>;

const CHALLENGE_SETTLE_ATTEMPTS = 10;
const CHALLENGE_SETTLE_INTERVAL_MS = 3000;

/** How a transact ended: the transaction it left, and the error to show when it did not go through. */
type TransactOutcome = { transaction: Transaction | null; error: string | undefined };

interface ProcessTransactableResult {
  success: boolean;
  isAntifraudError: boolean;
}

const processTransactable = async (
  config: TransactableConfig,
  payment: Payment,
  challengeContext: ChallengeContext | undefined,
  dispatch: AppDispatch,
  getState: () => RootState,
): Promise<ProcessTransactableResult> => {
  let response: AxiosResponse<Transaction>;
  try {
    const client = getClient(getState());
    response = await config.executeTransaction(client);
  } catch (err) {
    const errorMessage = getErrorMessageByType(err as AxiosError<any>);
    const isAntifraudError = errorMessage === CheckoutErrorsEnum.ANTIFRAUD;

    if (isAntifraudError) {
      return { success: false, isAntifraudError: true };
    }

    const errors = validationErrors(err);
    if (errors) {
      dispatch(setPaymentErrors(errors));
      if (config.shouldReloadOnValidationError) {
        dispatch(getPaymentStatus(payment)); // Reload in case the transactable is already expired
      }
      dispatch(setPaymentOngoing(false));
      return { success: false, isAntifraudError: false };
    }

    dispatch(
      setPaymentErrors({
        transactionError: [errorMessage],
      }),
    );
    dispatch(setPaymentOngoing(false));
    return { success: false, isAntifraudError: false };
  }

  if (requiresRedirect(response.data)) {
    const publicApiKey = publicApiTokenSelector(getState());
    storage.setValue(publicApiKey!, planneWidgetSaleReturnKey, config.storageId);
    dispatch(setPaymentOngoing(false));
    dispatch(changeLocation(response.data.paymentUrl));
  } else {
    let outcome: TransactOutcome = { transaction: response.data, error: undefined };
    if (response.data.externalAuthentication) {
      outcome = await followExternalAuthenticationChallenge(
        config,
        response.data,
        challengeContext,
        dispatch,
        getState,
      );
    } else {
      await config.refreshTransactable();
    }
    const { transaction, error } = outcome;

    clearSaleOrigin();
    if (transaction?.succeeded) {
      dispatch(setLastSuccessfulPayment());
      // The attempts belong to the purchase that just ended, not to whatever is bought next.
      dispatch(resetPaymentAttempts());
    } else if (error) {
      dispatch(setPaymentErrors({ transactionError: [error] }));
    }
    dispatch(setPaymentOngoing(false));
  }

  return { success: true, isAntifraudError: false };
};

/**
 * Shows the challenge a transaction came back waiting on and answers with the transaction as it stands
 * after it. The provider only says whether the challenge ended; whether the buyer passed it is read from
 * the transaction, which the buyer API return page has already pulled by the time the challenge ends.
 */
const followExternalAuthenticationChallenge = async (
  config: TransactableConfig,
  transaction: Transaction,
  challengeContext: ChallengeContext | undefined,
  dispatch: AppDispatch,
  getState: () => RootState,
): Promise<TransactOutcome> => {
  const challenge = transaction.externalAuthentication!;
  let result: ExternalAuthResult = { proceed: false, error: 'tds_generic_error' };

  if (challengeContext) {
    // Unlike the setup, a challenge returned by the transact is always shown to the buyer
    dispatch(setExternalAuthentication(challenge));
    try {
      const provider = ExternalAuthenticationProviderFactory.getProvider(challenge);
      result = await provider.handleAuthentication({
        ...challengeContext,
        paymentMethodExternalAuthentication: challenge,
      });
    } finally {
      dispatch(setExternalAuthentication(undefined));
    }
  } else {
    Bugsnag.notify({
      name: 'ExternalAuthChallenge',
      message: 'Transaction asked for a challenge without a payment option',
    });
  }

  if (!result.proceed) {
    await notifyChallengeAttempt(transaction.id, result, getState);
  }

  const settled = await waitForSettledTransaction(config);

  if (!result.proceed) {
    return { transaction: settled, error: result.error ?? 'tds_generic_error' };
  }

  // Still pending means the gateway has not answered yet; the transaction settles on the backend's side
  return {
    transaction: settled,
    error: isFailedTransaction(settled) ? 'tds_transaction_failed' : undefined,
  };
};

/**
 * Refreshes the transactable until its last transaction leaves Pending, or the attempts run out. The buyer
 * API return page pulls the transaction before the challenge ends, so the first refresh usually settles it;
 * the rest cover a gateway that takes longer to answer.
 */
const waitForSettledTransaction = async (config: TransactableConfig) => {
  for (let attempt = 1; ; attempt++) {
    const transaction = await config.refreshTransactable();
    if (!transaction?.pending || attempt >= CHALLENGE_SETTLE_ATTEMPTS) {
      return transaction;
    }

    await new Promise((resolve) => setTimeout(resolve, CHALLENGE_SETTLE_INTERVAL_MS));
  }
};

/** Best effort: the report only keeps what the checkout saw; the transaction itself does not change. */
const notifyChallengeAttempt = async (transactionId: string, result: ExternalAuthResult, getState: () => RootState) => {
  try {
    const state = getState();
    const { buyerToken } = state.auth;

    await getClient(state).transactions.notifyTransactionExternalAuthenticationAttempt(
      transactionId,
      {
        reason: {
          error: result.error,
          externalError: result.externalError,
          externalResponse: result.externalAuthentication,
        },
      },
      buyerToken ? bearerAuth(buyerToken) : undefined,
    );
  } catch (error) {
    Bugsnag.notify(
      { name: 'ExternalAuthChallengeNotifyError', message: 'Error notifying the challenge attempt' },
      (event) => {
        event.addMetadata('error', { error });
      },
    );
  }
};

const processNotify = async (
  config: TransactableConfig,
  errors: Record<string, unknown>,
  getState: () => RootState,
) => {
  try {
    const client = getClient(getState());
    await config.executeNotify(client, errors);
  } catch (error) {
    console.log(error);
    Bugsnag.notify(
      {
        name: 'ProcessNotifyError',
        message: 'Error processing notify in transactable',
      },
      (event) => {
        event.addMetadata('error', {
          error,
        });
      },
    );
  }
};

const getMustUseExternalAuthentication = (antifraudErrorCount: number, paymentOption: PaymentOption) => {
  // Backend contract: paymentAuthSettings is required when requiresExternalAuthentication is true,
  // and executionRule will always have a default value. Monitor for violations.
  if (isMissingExecutionRule(paymentOption)) {
    Bugsnag.notify(
      {
        name: 'MissingExecutionRuleError',
        message: 'executionRule is undefined when requiresExternalAuthentication is true',
      },
      (event) => {
        event.addMetadata('paymentOption', {
          paymentMethodId: paymentOption.paymentMethod.id,
          requiresExternalAuthentication: paymentOption.requiresExternalAuthentication,
          hasPaymentAuthSettings: !!paymentOption.paymentAuthSettings,
          executionRule: paymentOption.paymentAuthSettings?.executionRule,
        });
      },
    );
  }

  return shouldUseExternalAuthentication(antifraudErrorCount, paymentOption);
};

export const processPayment = (payment: Payment) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const { order, sale, directLink, saleChange, paymentMethodId, paymentCard } = payment;
  const state = getState();
  const {
    payment: { paymentOngoing },
    auth: { buyerToken, buyer },
  } = state;

  if (paymentOngoing || (requiresBuyerToken({ order, sale, directLink, saleChange }) && buyerToken == null)) {
    return;
  }

  const buyerAuth = buyerToken ? bearerAuth(buyerToken) : undefined;
  const sessionId = globalParameterSelector('session-id')(state);

  const data: TransactionRequest = {
    paymentMethodId: paymentMethodId!,
    paymentCard,
    returnUrl: location.href,
    deviceInfo: {
      language: navigator.language,
      screenHeight: screen.height,
      screenWidth: screen.width,
      timeZoneOffset: new Date().getTimezoneOffset(),
    },
    sessionId,
  };

  dispatch(setPaymentOngoing(true));
  dispatch(markChargeAttempt());
  dispatch(setPaymentErrors({}));

  const paymentOptions = paymentOptionSelector(payment)(state);
  const paymentOption = paymentOptions?.list.find((po) => po.paymentMethod.id == paymentMethodId);

  let transactableConfig: TransactableConfig;

  if (saleChange) {
    transactableConfig = {
      paymentMethodId: paymentMethodId!,
      executeTransaction: (client) => {
        return client.transactions.transactSaleChange(saleChange.id, data, bearerAuth(buyerToken!));
      },
      executeNotify: async (client, errors) => {
        await client.transactions.notifySaleChangeExternalAuthenticationAttempt(
          saleChange.id,
          {
            paymentMethodId: paymentMethodId!,
            reason: { ...errors },
          },
          bearerAuth(buyerToken!),
        );
      },
      storageId: saleChange.id,
      refreshTransactable: async () => {
        const { data: updatedSaleChange } = await getClient(getState()).saleChanges.getSaleChange(
          saleChange.id,
          bearerAuth(buyerToken!),
        );

        dispatch(setProcessedSaleChange(updatedSaleChange));
        // Refetch the order so SaleDisplay shows updated reschedule (Applied sale change)
        if (order) {
          await loadOrderAsCurrent(dispatch, getState, order.id, setCurrentOrder);
        }

        return updatedSaleChange.lastTransaction;
      },
      shouldReloadOnValidationError: true,
    };
  } else if (sale) {
    transactableConfig = {
      paymentMethodId: paymentMethodId!,
      executeTransaction: (client) => {
        return client.transactions.transactSale(sale.id, data, buyerAuth);
      },
      executeNotify: async (client, errors) => {
        await client.transactions.notifySaleExternalAuthenticationAttempt(
          sale.id,
          {
            paymentMethodId: paymentMethodId!,
            reason: { ...errors },
          },
          buyerAuth,
        );
      },
      storageId: sale.id,
      refreshTransactable: async () => {
        const [updatedSale] = await Promise.all([
          loadSaleForDirectLink(dispatch, getState, sale.id, setProcessedSale),
          ...(directLink ? [updateDirectLinkAfterTransaction(directLink, dispatch, getState)] : []),
        ]);

        return updatedSale?.lastTransaction ?? null;
      },
      shouldReloadOnValidationError: true,
    };
  } else if (order) {
    transactableConfig = {
      paymentMethodId: paymentMethodId!,
      executeTransaction: (client) => client.transactions.transactOrder(order.id, data, buyerAuth),
      executeNotify: async (client, errors) => {
        await client.transactions.notifyOrderExternalAuthenticationAttempt(
          order.id,
          {
            paymentMethodId: paymentMethodId!,
            reason: { ...errors },
          },
          buyerAuth,
        );
      },
      storageId: order.id,
      refreshTransactable: async () => {
        // A payment_complete link makes the payment step hand the host the stored order, so it must land first.
        const updatedOrder = await reloadOrder({ order, directLink }, setProcessedOrder, dispatch, getState);

        if (directLink) {
          await updateDirectLinkAfterTransaction(directLink, dispatch, getState);
        }

        return updatedOrder?.lastTransaction ?? null;
      },
      shouldReloadOnValidationError: true,
    };
  } else if (directLink) {
    transactableConfig = {
      paymentMethodId: paymentMethodId!,
      executeTransaction: (client) => client.transactions.transactDirectLink(directLink.id, data),
      executeNotify: async (client, errors) => {
        await client.transactions.notifyDirectLinkExternalAuthenticationAttempt(directLink.id, {
          paymentMethodId: paymentMethodId!,
          reason: { ...errors },
        });
      },
      storageId: directLink.code,
      refreshTransactable: async () =>
        (await updateDirectLinkAfterTransaction(directLink, dispatch, getState)).lastTransaction,
      shouldReloadOnValidationError: false,
    };
  } else {
    // No valid transactable: reset payment state and exit
    dispatch(setPaymentOngoing(false));
    throw new Error('No valid transactable');
  }

  try {
    let shouldContinue = true;
    let lastResult: ProcessTransactableResult | undefined;
    let antifraudErrorCount = 0;
    let loopIterations = 0;
    const MAX_LOOP_ITERATIONS = 5; // Safety limit to prevent infinite loops

    while (shouldContinue) {
      loopIterations++;
      if (loopIterations > MAX_LOOP_ITERATIONS) {
        shouldContinue = false;
        dispatch(setPaymentOngoing(false));
        dispatch(setPaymentErrors({ transactionError: [CheckoutErrorsEnum.GENERIC] }));
        Bugsnag.notify({
          name: 'ProcessPaymentInfiniteLoop',
          message: 'Maximum loop iterations reached in processPayment',
        });
        return;
      }

      const mustUseExternalAuthentication =
        !!buyer && !!paymentOption && getMustUseExternalAuthentication(antifraudErrorCount, paymentOption);

      if (mustUseExternalAuthentication) {
        const MAX_ATTEMPTS = 2;
        const transactable = {
          sale,
          order,
          directLink,
          saleChange,
        };

        const { proceed, error, externalError, externalAuthentication } = await externalAuthDecision({
          widgetUrl: state.userPreferences.widgetUrl,
          paymentOption,
          transactable,
          data,
          maxAttempts: MAX_ATTEMPTS,
          dispatch,
        });

        if (!proceed) {
          dispatch(setPaymentOngoing(false));
          dispatch(setPaymentErrors({ transactionError: [error ?? ''] }));

          await processNotify(
            transactableConfig,
            {
              error,
              externalError,
              externalResponse: externalAuthentication,
            },
            getState,
          );

          return;
        }

        if (externalAuthentication) {
          data.externalAuthentication = externalAuthentication;
        }
      }

      const challengeContext: ChallengeContext | undefined = paymentOption && {
        widgetUrl: state.userPreferences.widgetUrl,
        paymentOption,
        transactable: { sale, order, directLink, saleChange },
        data,
        dispatch,
        buyer,
      };

      lastResult = await processTransactable(transactableConfig!, payment, challengeContext, dispatch, getState);

      if (lastResult.success) {
        shouldContinue = false;
      } else if (lastResult.isAntifraudError && antifraudErrorCount === 0) {
        antifraudErrorCount++;
        // Continue loop to retry with external authentication if needed
      } else {
        // Handle second antifraud error or other errors
        if (lastResult.isAntifraudError) {
          dispatch(setPaymentErrors({ transactionError: [CheckoutErrorsEnum.ANTIFRAUD] }));
        }
        dispatch(setPaymentOngoing(false));
        shouldContinue = false;
      }
    }

    return lastResult;
  } catch (error) {
    dispatch(setPaymentOngoing(false));
    console.log(error);

    Bugsnag.notify(
      {
        name: 'ProcessPaymentError',
        message: `Error processing payment`,
      },
      (event) => {
        event.addMetadata('error', {
          error,
        });
      },
    );
    return;
  }
};

export const getPaymentStatus = (payment: Payment) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const { order, sale, directLink, saleChange } = payment;

  const state = getState();
  const {
    auth: { buyerToken },
  } = state;

  if (requiresBuyerToken({ order, sale, directLink, saleChange }) && buyerToken == null) {
    return;
  }

  if (saleChange) {
    const { data: updated } = await getClient(state).saleChanges.getSaleChange(saleChange.id, bearerAuth(buyerToken!));
    dispatch(setCurrentSaleChange(updated));
  } else if (order) {
    await reloadOrder({ order, directLink }, setCurrentOrder, dispatch, getState);
  } else if (sale) {
    await loadSaleForDirectLink(dispatch, getState, sale.id, setProcessedSale);
  } else if (directLink) {
    const { data: newDirectLink } = await getClient(state).directLinks.getDirectLink(directLink.code);
    dispatch(setDirectLink(newDirectLink));
  }
};

export type { Transactable } from '@src/domains/transactables';

export const loadPaymentOptions = (tr: Transactable) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const { order, sale, directLink, saleChange } = tr;

  const state = getState();
  const {
    auth: { buyerToken },
  } = state;

  const trKey = resolveTransactableKey(tr);

  if (requiresBuyerToken({ order, sale, directLink, saleChange }) && buyerToken == null) {
    return;
  }

  if (!saleChange && !order && !sale && !directLink) {
    dispatch(setPaymentOptionsLoad([], trKey, false));
    return;
  }

  dispatch(setPaymentOptionsLoad([], trKey, true));

  const buyerAuth = buyerToken ? bearerAuth(buyerToken) : undefined;

  try {
    const sessionId = globalParameterSelector('session-id')(state);

    // Ideally, should be paginated, but is unlikely we'll have more than 100 payment options
    if (saleChange) {
      const { data: pms } = await getClient(state).paymentOptions.getSaleChangePaymentOptions(
        saleChange.id,
        sessionId,
        0,
        100,
        bearerAuth(buyerToken!),
      );
      dispatch(setPaymentOptionsLoad(pms, trKey, false));
    } else if (tr.order) {
      const { data: pms } = await getClient(state).paymentOptions.getOrderPaymentOptions(
        tr.order.id,
        sessionId,
        0,
        100,
        buyerAuth,
      );
      dispatch(setPaymentOptionsLoad(pms, trKey, false));
    } else if (sale) {
      const { data: pms } = await getClient(state).paymentOptions.getSalePaymentOptions(
        sale.id,
        sessionId,
        0,
        100,
        buyerAuth,
      );
      dispatch(setPaymentOptionsLoad(pms, trKey, false));
    } else if (directLink) {
      const { data: pms } = await getClient(state).paymentOptions.getDirectLinkPaymentOptions(
        directLink.id,
        sessionId,
        0,
        100,
      );
      dispatch(setPaymentOptionsLoad(pms, trKey, false));
    }
  } catch (error) {
    // Without this `loading` stays on forever and the screen never leaves the skeleton:
    // an order in an invalid state, for one, takes the call down and the buyer never
    // reaches the notice. An empty list is what PaymentMethodList knows how to show.
    dispatch(setPaymentOptionsLoad([], trKey, false));

    Bugsnag.notify(
      {
        name: 'LoadPaymentOptionsError',
        message: 'Error loading payment options for transactable',
      },
      (event) => {
        event.addMetadata('error', { error, transactable: trKey });
      },
    );
  }
};

export const paymentOptionSelector = (tr: Transactable) => (state: RootState) => {
  const trKey = resolveTransactableKey(tr);
  return state.payment.paymentOptions[trKey];
};

export default paymentSlice.reducer;
